// src/admin/pages/AdminNotifications.js
import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import AdminSidebar from "../AdminSidebar";
import AdminHeader from "../AdminHeader";
import {
    FaBell,
    FaExclamationTriangle,
    FaShoppingCart,
    FaBoxOpen,
    FaEnvelope,
    FaCheckCircle
} from "react-icons/fa";
import { 
  getAdminNotifications,
  markNotificationRead,
  deleteNotification,
} from "../../services/adminNotifications.api";
import { AdminLanguageContext } from "../context/AdminLanguageContext";

function AdminNotifications() {
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("All");

    const [page, setPage] = useState(1);
    const perPage = 10;

    const { lang } = useContext(AdminLanguageContext);
    
    const unreadCounts = {
  order: notifications.filter(
    (n) => n.type === "order" && !n.read
  ).length,
  stock: notifications.filter(
    (n) => n.type === "stock" && !n.read
  ).length,
  message: notifications.filter(
    (n) => n.type === "message" && !n.read
  ).length,
};

const handleRowClick = (n) => {
  if (n.type === "stock") {
    navigate("/admin/inventory");
  } else if (n.type === "order") {
    navigate("/admin/orders");
  } else if (n.type === "message") {
    navigate("/admin/messages");
  }
};

    const TEXT = {
        EN: {
            title: "Notifications",
            markAll: "Mark All as Read",
            filters: {
                All: "All",
                order: "Orders",
                stock: "Stock",
                message: "Messages",
            },
            loading: "Loading notifications...",
            empty: "No notifications found.",
            markRead: "Mark Read",

            // notification titles/messages
orderTitle: "New Order Received",
orderMsg: "A new order has been placed.",
stockTitle: "Low Stock",
stockMsg: "A product is running low in stock.",
messageTitle: "New Customer Message",
messageMsg: "A new support message was submitted.",
delete: "Delete",
        },
        AR: {
            title: "الإشعارات",
            markAll: "تحديد الكل كمقروء",
            filters: {
                All: "الكل",
                order: "الطلبات",
                stock: "المخزون",
                message: "الرسائل",
                system: "النظام",
            },
            loading: "جاري تحميل الإشعارات...",
            empty: "لا توجد إشعارات.",
            markRead: "تحديد كمقروء",

            orderTitle: "طلب جديد",
orderMsg: "تم إنشاء طلب جديد.",
stockTitle: "مخزون منخفض",
stockMsg: "يوجد منتج أوشك على النفاد من المخزون.",
messageTitle: "رسالة جديدة",
messageMsg: "تم استلام رسالة دعم جديدة.",
delete: "حذف",
        },
    }[lang];

    useEffect(() => {
  setPage(1);
}, [filter]);

   useEffect(() => {
  setLoading(true);
  getAdminNotifications()
    .then((rows) => {
      const mapped = rows
  .filter((n) => n.type !== "other")
  .sort((a, b) => new Date(b.created_at) - new Date(a.created_at)) // ✅ NEWEST FIRST
  .map((n) => ({
    id: n.id,
    type:
      n.type === "new_order"
        ? "order"
        : n.type === "new_message"
        ? "message"
        : n.type === "low_stock"
        ? "stock"
        : "other",
    title:
        lang === "AR"
          ? n.type === "new_order"
            ? TEXT.orderTitle
            : n.type === "new_message"
            ? TEXT.messageTitle
            : TEXT.stockTitle
          : n.title,

      message:
        lang === "AR"
          ? n.type === "new_order"
            ? TEXT.orderMsg
            : n.type === "new_message"
            ? TEXT.messageMsg
            : TEXT.stockMsg
          : n.body,
    date: new Date(n.created_at).toLocaleString(
          lang === "AR" ? "ar-EG" : "en-US"
        ),
    read: n.is_read,
    icon:
      n.type === "new_order" ? (
        <FaShoppingCart className="text-gray-600" />
      ) : n.type === "new_message" ? (
        <FaEnvelope className="text-gray-600" />
      ) : (
        <FaBoxOpen className="text-gray-600" />
      ),
  }));


      setNotifications(mapped);
    })
    .finally(() => setLoading(false));
}, [lang]);


    const filtered = notifications.filter((n) =>
        filter === "All" ? true : n.type === filter
    );

    const totalPages = Math.ceil(filtered.length / perPage);

const paginated = filtered.slice(
  (page - 1) * perPage,
  page * perPage
);

    const markRead = async (id) => {
  await markNotificationRead(id);
  setNotifications((prev) =>
    prev.map((n) => (n.id === id ? { ...n, read: true } : n))
  );
};


    const markAll = async () => {
  const unreadIds = notifications.filter((n) => !n.read).map((n) => n.id);

  await Promise.all(unreadIds.map((id) => markNotificationRead(id)));

  setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
};


    return (
    <div className={`flex bg-[#f1f1f1] min-h-screen ${isOpen ? "overflow-hidden" : ""}`}>
        <AdminSidebar isOpen={isOpen} setIsOpen={setIsOpen} />

        <div className={`flex-1 ${lang === "EN" ? "lg:ml-64" : ""} w-full`}>
            <AdminHeader setIsOpen={setIsOpen} />

            <main
                  dir={lang === "AR" ? "rtl" : "ltr"}
                  className="p-4 sm:p-6 animate-fadeSlide"
                >

                    {/* HEADER */}
                   <div className="flex items-center justify-between mb-6">
                        <h1
                          className={`text-[20px] font-semibold text-gray-900 flex items-center gap-2 ${
                            lang === "AR" ? "text-right" : ""
                          }`}
                        >
                          <FaBell className="text-gray-900 text-[18px]" />
                          <span>{TEXT.title}</span>
                        </h1>

                        <button
                            onClick={markAll}
                            className="
h-8 px-3
bg-white
border border-gray-300
rounded-lg
shadow-[0_1px_0_rgba(0,0,0,0.04),0_2px_5px_rgba(0,0,0,0.06)]
text-sm font-medium text-gray-900
hover:bg-gray-50
active:shadow-[inset_0_2px_4px_rgba(0,0,0,0.12)]
transition
"
                        >
                            {TEXT.markAll}
                        </button>
                    </div>

                    {/* FILTERS */}
                    <div
                        className={`flex flex-wrap gap-3 mb-5 ${
                          lang === "AR" ? "justify-end" : "justify-start"
                        }`}
                      >
  {["All", "order", "stock", "message"].map((f) => {
    const count = unreadCounts[f];

    return (
      <div key={f} className="relative">
        <button
          onClick={() => setFilter(f)}
          className={`
px-3 py-[5px]
text-[13px] font-medium
rounded-md
transition
${
  filter === f
    ? "bg-gray-200 text-gray-900 shadow-inner"
    : "bg-white text-gray-600 hover:bg-gray-100"
}
`}
        >
          {TEXT.filters[f]}
        </button>

        {/* 🔔 Unread badge */}
        {count > 0 && (
          <span
            className="
              absolute -top-2 -right-2
              min-w-[20px] h-[20px]
              px-1
              rounded-full
              bg-red-500 shadow-sm text-white
              text-[11px]
              flex items-center justify-center
              font-semibold
            "
          >
            {count}
          </span>
        )}
      </div>
    );
  })}
</div>


                    {/* LIST */}
                    <div className="bg-white
border border-gray-200
rounded-[14px]
shadow-[0_1px_0_rgba(0,0,0,0.04),0_8px_24px_rgba(0,0,0,0.08)]">
                        {loading ? (
  <div className="animate-pulse divide-y">
    {[...Array(6)].map((_, i) => (
      <div key={i} className="px-4 py-4 flex justify-between gap-3">

        {/* LEFT */}
        <div className="flex gap-3 flex-1">
          <div className="w-10 h-10 bg-gray-200 rounded-xl" />
          <div className="flex-1 space-y-2">
            <div className="h-3 w-32 bg-gray-200 rounded" />
            <div className="h-2 w-48 bg-gray-200 rounded" />
            <div className="h-2 w-20 bg-gray-200 rounded" />
          </div>
        </div>

        {/* RIGHT */}
        <div className="flex gap-2">
          <div className="w-16 h-6 bg-gray-200 rounded" />
          <div className="w-16 h-6 bg-gray-200 rounded" />
        </div>

      </div>
    ))}
  </div>
) :  filtered.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-12 text-gray-500 gap-2">
  <FaBell className="text-2xl opacity-40" />
  <span className="text-sm">{TEXT.empty}</span>
</div>
                        ) : (
                            paginated.map((n) => (
                                <div
                                  key={n.id}
                                  onClick={() => handleRowClick(n)}
                                  className={`
                                        px-4 py-3
                                        border-b border-gray-100
                                        flex items-start sm:items-center
                                        justify-between
                                        gap-3
                                        transition
                                        cursor-pointer
                                        hover:shadow-sm active:scale-[0.995]
                                        ${!n.read ? "bg-[#fff7e6]" : "bg-white hover:bg-gray-50"}
                                      `}
                                >
                                 {/* RIGHT SIDE (ICON + TEXT) */}
<div
  className={`flex items-start gap-3 ${
    lang === "AR" ? "text-right" : ""
  }`}
>
  <div className="w-10 h-10 shrink-0 flex items-center justify-center rounded-xl bg-gray-100 text-gray-600">
    {n.icon}
  </div>

  <div className="min-w-0">
    <h3 className="text-[13.5px] font-semibold text-gray-900 leading-tight">
      {n.title}
    </h3>
    <p className="text-[12.5px] text-gray-600 leading-snug">
      {n.message}
    </p>
    <span className="text-[11px] text-gray-400 mt-0.5 block">
      {n.date}
    </span>
  </div>
</div>
                                   <div
  className={`flex gap-2 ${
    lang === "AR"
      ? "flex-row-reverse justify-start"
      : ""
  }`}
  onClick={(e) => e.stopPropagation()}
>
  <button
    onClick={async (e) => {
      e.stopPropagation();
      await deleteNotification(n.id);
      setNotifications((prev) =>
        prev.filter((x) => x.id !== n.id)
      );
    }}
    className="text-[12px] px-2 py-1 rounded-md text-gray-500 hover:bg-red-50 hover:text-red-600 transition"
  >
    {TEXT.delete}
  </button>

  {!n.read && (
    <button
      onClick={(e) => {
        e.stopPropagation();
        markRead(n.id);
      }}
      className="text-[12px] px-2 py-1 rounded-md flex items-center gap-1 text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition"
    >
      <FaCheckCircle /> {TEXT.markRead}
    </button>
  )}
</div>
                                </div>
                            ))
                        )}
                    </div>

{!loading && filtered.length > 0 && totalPages > 1 && (
  <div
    className={`flex items-center justify-between mt-4 px-2 text-sm ${
      lang === "AR" ? "flex-row-reverse" : ""
    }`}
  >
    {/* PREV */}
    <button
      onClick={() => setPage((p) => Math.max(1, p - 1))}
      disabled={page === 1}
      className={`px-3 py-1 border rounded-md bg-white ${
        page === 1 ? "opacity-40" : ""
      }`}
    >
      {lang === "AR" ? "السابق" : "Prev"}
    </button>

    {/* PAGE */}
    <span className="font-medium">
      {page} / {totalPages}
    </span>

    {/* NEXT */}
    <button
      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
      disabled={page === totalPages}
      className={`px-3 py-1 border rounded-md bg-white ${
        page === totalPages ? "opacity-40" : ""
      }`}
    >
      {lang === "AR" ? "التالي" : "Next"}
    </button>
  </div>
)}

                </main>
            </div>
        </div>
    );
}

export default AdminNotifications;